import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEOHead from '../components/SEOHead';

const AccessibilityPolicy = () => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';

  const breadcrumbs = [
    { name: 'Home', url: siteUrl },
    { name: 'Accessibility Policy', url: `${siteUrl}/accessibility` }
  ];

  return (
    <>
      <SEOHead
        title="Accessibility Policy | DonorsHouse - Digital Accessibility Commitment"
        description="Learn about DonorsHouse's commitment to digital accessibility. We conform to WCAG 2.2 Level AA standards to ensure our platform is accessible to all users."
        keywords="accessibility policy, WCAG, digital accessibility, accessible website, assistive technologies, inclusive design"
        breadcrumbs={breadcrumbs}
      />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Accessibility Policy</h1>
        
        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">Our Commitment</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              DonorsHouse is committed to ensuring digital accessibility for people with disabilities. We are 
              continually improving the user experience for everyone and applying the relevant accessibility standards 
              to ensure we provide equal access to all our users.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">Conformance Status</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p className="mb-3">
              DonorsHouse conforms to the Web Content Accessibility Guidelines (WCAG) 2.2 Level AA. These guidelines 
              explain how to make web content more accessible for people with disabilities and user-friendly for everyone.
            </p>
            <p>
              We have implemented the following accessibility features:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Proper color contrast ratios (minimum 4.5:1 for normal text)</li>
              <li>Keyboard navigation support</li>
              <li>ARIA labels and landmarks</li>
              <li>Focus indicators for interactive elements</li>
              <li>Skip navigation links</li>
              <li>Alternative text for images</li>
              <li>Resizable text without loss of functionality</li>
              <li>Consistent navigation across all pages</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">Assistive Technologies</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>DonorsHouse is designed to be compatible with the following assistive technologies:</p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Screen readers (JAWS, NVDA, VoiceOver)</li>
              <li>Screen magnification software</li>
              <li>Speech recognition software</li>
              <li>Alternative input devices</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">Limitations and Alternatives</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              Despite our best efforts to ensure accessibility, there may be some limitations. If you experience 
              any difficulty accessing any part of this website, please contact us. We will make every effort to 
              provide you with the information in an alternative format.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">Third-Party Content</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              Some content on our website may be provided by third parties not within our control. We strive to 
              only include accessible third-party content, but cannot guarantee full accessibility of all external 
              resources.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">Assessment and Testing</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              We regularly assess the accessibility of DonorsHouse through:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li>Automated accessibility testing tools</li>
              <li>Manual testing with assistive technologies</li>
              <li>User feedback and testing</li>
              <li>Regular audits and updates</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-gray-900">Feedback and Contact</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p className="mb-3">
              We welcome your feedback on the accessibility of DonorsHouse. Please let us know if you encounter 
              accessibility barriers:
            </p>
            <p className="font-semibold">Email: https://newabilities.org/contact</p>
            <p className="mt-3 text-sm">
              We aim to respond to accessibility feedback within 5 business days.
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-gray-600 mt-8">Last Reviewed: January 2025</p>
      </div>
    </div>
    </>
  );
};

export default AccessibilityPolicy;
